import { useEffect, useState } from 'react';
import type { WorldviewVersion } from '../lib/storage';
import { DiffView } from './DiffView';

interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  versions: WorldviewVersion[];
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function HistoryModal({ open, onClose, versions }: HistoryModalProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) setExpanded(null);
  }, [open]);

  if (!open) return null;

  const ordered = [...versions].sort((a, b) => b.version - a.version);
  const currentVersion = ordered[0]?.version;
  const byVersion = new Map(versions.map((v) => [v.version, v]));

  return (
    <div
      className="history-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Worldview version history"
    >
      <div
        className="history-modal__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="history-modal__panel">
        <button
          type="button"
          className="history-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <p className="history-modal__eyebrow">— Version History —</p>
        <h2 className="history-modal__title">
          {ordered.length} {ordered.length === 1 ? 'version' : 'versions'}
        </h2>

        <ul className="history-modal__list">
          {ordered.map((v) => {
            const isOpen = expanded === v.version;
            const previous = byVersion.get(v.version - 1);
            return (
              <li
                key={v.version}
                className={`history-version${isOpen ? ' history-version--open' : ''}`}
              >
                <button
                  type="button"
                  className="history-version__header"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setExpanded((cur) => (cur === v.version ? null : v.version))
                  }
                >
                  <span className="history-version__label">
                    v{v.version}
                    {v.version === currentVersion && (
                      <span className="history-version__badge">Current</span>
                    )}
                  </span>
                  <span className="history-version__date">
                    {formatDate(v.createdAt)}
                  </span>
                  <span
                    className="history-version__chevron"
                    aria-hidden="true"
                  >
                    {isOpen ? '▾' : '▸'}
                  </span>
                </button>

                {v.feedback && (
                  <p className="history-version__feedback">
                    <span className="history-version__feedback-label">
                      Refined from feedback:
                    </span>{' '}
                    &ldquo;{v.feedback.note}&rdquo;
                  </p>
                )}

                {isOpen && (
                  <div className="history-version__body">
                    {v.feedback && (
                      <details className="history-version__exchange">
                        <summary>View the exchange that triggered this revision</summary>
                        <p className="history-version__exchange-line">
                          <strong>You asked:</strong> {v.feedback.question}
                        </p>
                        <p className="history-version__exchange-line">
                          <strong>Got back:</strong> {v.feedback.response}
                        </p>
                      </details>
                    )}

                    {previous ? (
                      <>
                        <p className="history-version__diff-label">
                          Changes from v{previous.version} → v{v.version}
                        </p>
                        <DiffView before={previous.content} after={v.content} />
                        <details className="history-version__full">
                          <summary>View full file (v{v.version})</summary>
                          <pre className="history-version__content">{v.content}</pre>
                        </details>
                      </>
                    ) : (
                      <pre className="history-version__content">{v.content}</pre>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
